import type { AgPlanerMap } from '../api/types';

/** Kachelgröße der Spielkarte in Pixeln bei Zoom 1 (aus den Originalsprites). */
export const TILE_W = 53;
export const TILE_H = 38;

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 2;
/** Ab diesem Zoom werden die Originalsprites gezeichnet, darunter die Übersicht (1 Pixel je Kachel hochskaliert). */
export const SPRITE_ZOOM = 0.3;

/** Dorf-Level der Spielkarte aus den Punkten (identisch zu TWMap.map.getLevelForVillagePoints). */
export function villageLevel(points: number): number {
  if (points < 300) return 1;
  if (points < 1000) return 2;
  if (points < 3000) return 3;
  if (points < 9000) return 4;
  if (points < 11000) return 5;
  return 6;
}

/**
 * Immer die deckenden Sprites (`v`-Serie, Bonusdörfer `b`-Serie). Das Spiel
 * zeichnet Barbarendörfer mit den verblassten `_left`-Sprites -- die sind hier
 * ausdrücklich nicht gewünscht (keine Transparenz); Barbarendörfer erkennt man
 * stattdessen an der grauen Markierung.
 */
export function villageSpriteName(points: number, bonus: boolean): string {
  return `${bonus ? 'b' : 'v'}${villageLevel(points)}`;
}

/** Bildindex (TWMap.images des Spiels) -> Dateiname des Geländesprites; Unbekanntes wird zu Gras. */
export function terrainSpriteName(index: number): string {
  if (index >= 0 && index <= 3) return `gras${index + 1}`;
  if (index >= 28 && index <= 31) return `berg${index - 27}`;
  if (index >= 32 && index <= 47) return `forest${(index - 32).toString(2).padStart(4, '0')}`;
  if (index === 48) return 'see';
  return 'gras1';
}

export const ALL_SPRITE_NAMES: string[] = (() => {
  const names = ['gras1', 'gras2', 'gras3', 'gras4', 'berg1', 'berg2', 'berg3', 'berg4', 'see'];
  for (let level = 1; level <= 6; level++) names.push(`v${level}`, `b${level}`);
  for (let bits = 0; bits < 16; bits++) names.push(`forest${bits.toString(2).padStart(4, '0')}`);
  return names;
})();

/** Farben der Spiel-Legende. */
export const COLORS = {
  self: '#f0c800',
  ownTribe: '#2b4bff',
  barbarian: '#969696',
  other: '#823c0a',
} as const;

export type ColorMode = 'beziehung' | 'staemme';

/** Stabile Farbe je Stamm für die Ansicht "nach Stämmen". */
export function tribeColor(tribeGameId: number): string {
  const hue = (tribeGameId * 137.508) % 360;
  return `hsl(${hue.toFixed(0)} 70% 52%)`;
}

export interface OwnerInfo {
  playerIndex: number;
  tribeIndex: number;
  isSelf: boolean;
  isOwnTribe: boolean;
}

/** Vorberechnete Nachschlagetabellen über alle Dörfer -- einmal je Karte, nicht je Frame. */
export interface MapIndex {
  width: number;
  height: number;
  /** x + y * width -> Dorfindex, -1 = kein Dorf. */
  cell: Int32Array;
  /** Dorfindex -> Farbe (CSS), `null` = Barbarendorf bei Beziehungsansicht (keine Markierung). */
  colorByVillage: Array<string | null>;
  ownByVillage: Uint8Array;
}

export function buildMapIndex(map: AgPlanerMap, mode: ColorMode): MapIndex {
  const { width, height } = map.extent;
  const cell = new Int32Array(width * height).fill(-1);
  const ownTribes = new Set(map.own.tribeIndexes);
  const ownPlayers = new Set(map.own.playerIndexes);
  const colorByVillage: Array<string | null> = new Array(map.villages.length).fill(null);
  const ownByVillage = new Uint8Array(map.villages.length);

  map.villages.forEach((village, i) => {
    const [x, y, , playerIndex] = village;
    if (x >= 0 && x < width && y >= 0 && y < height) cell[x + y * width] = i;

    if (playerIndex < 0) {
      colorByVillage[i] = mode === 'beziehung' ? COLORS.barbarian : null;
      return;
    }
    const player = map.players[playerIndex]!;
    const tribeIndex = player[2];
    const isSelf = playerIndex === map.own.selfPlayerIndex;
    const isOwn = isSelf || ownPlayers.has(playerIndex) || (tribeIndex >= 0 && ownTribes.has(tribeIndex));
    ownByVillage[i] = isOwn ? 1 : 0;

    if (mode === 'staemme') {
      colorByVillage[i] = tribeIndex >= 0 ? tribeColor(map.tribes[tribeIndex]![0]) : COLORS.other;
    } else if (isSelf) {
      colorByVillage[i] = COLORS.self;
    } else if (isOwn) {
      colorByVillage[i] = COLORS.ownTribe;
    } else {
      colorByVillage[i] = COLORS.other;
    }
  });

  return { width, height, cell, colorByVillage, ownByVillage };
}

export function formatPoints(points: number): string {
  return points.toLocaleString('de-DE');
}

/** "567|521" oder "567 521" -> Koordinate. */
export function parseCoordinates(text: string): { x: number; y: number } | null {
  const match = text.trim().match(/^(\d{1,4})\s*[|:;,\s]\s*(\d{1,4})$/);
  return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
}

export function continentOf(x: number, y: number): number {
  return Math.floor(y / 100) * 10 + Math.floor(x / 100);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Ein Angriffsbefehl als Pfeil Quelldorf -> Zieldorf (Koordinaten in Kacheln). */
export interface MapArrow {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  color: string;
  /** Gestrichelt gezeichnet (verplante AGs, damit sie sich von den Off-Pfeilen unterscheiden). */
  dashed?: boolean;
}

/** Ringmarkierung um ein Dorf (z. B. mögliche AG-Kandidaten). */
export interface MapMarker {
  x: number;
  y: number;
  color: string;
}

/** Datum/Uhrzeit in deutscher Spielzeit (Europe/Berlin), auf Wunsch mit Millisekunden. */
export function formatDateTime(ms: number, withMs = false): string {
  const text = new Date(ms).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return withMs ? `${text}.${String(((ms % 1000) + 1000) % 1000).padStart(3, '0')}` : text;
}

/** Sekunden als h:mm:ss. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** So viele AGs braucht ein Zieldorf, damit der AG-Zug komplett ist (4 AGs). */
export const NOBLES_PER_TARGET = 4;
